import { useCallback } from 'react';
import { resolveColor } from '../tokens/color';
import { confettiColors } from '../tokens/semantic';

type Origin = { x: number; y: number };

const reducedMotion = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Center of an element as a 0–1 viewport origin for confetti. */
export function originOf(el: Element | null | undefined): Origin {
  if (!el || typeof window === 'undefined') return { x: 0.5, y: 0.6 };
  const r = el.getBoundingClientRect();
  return {
    x: (r.left + r.width / 2) / window.innerWidth,
    y: (r.top + r.height / 2) / window.innerHeight,
  };
}

/**
 * Delight moments (docs/06 › Motion). All of them are skipped under prefers-reduced-motion.
 * - `claim(el)`: a small burst (~40 particles) from the button + a 10ms haptic tap.
 * - `big()`: a one-time full burst (onboarding finished, everything bought).
 * canvas-confetti is loaded lazily so it never weighs down first paint.
 */
export function useCelebrate() {
  const fire = useCallback(
    async (opts: {
      origin: Origin;
      particleCount: number;
      spread: number;
      startVelocity: number;
    }) => {
      if (reducedMotion()) return;
      const { default: confetti } = await import('canvas-confetti');
      await confetti({
        ...opts,
        colors: confettiColors.map(resolveColor),
        ticks: 120,
        scalar: 0.9,
        disableForReducedMotion: true,
        zIndex: 60,
      });
    },
    [],
  );

  const claim = useCallback(
    (el?: Element | null) => {
      if (reducedMotion()) return;
      navigator.vibrate?.(10);
      void fire({ origin: originOf(el), particleCount: 40, spread: 60, startVelocity: 28 });
    },
    [fire],
  );

  const big = useCallback(() => {
    void fire({ origin: { x: 0.5, y: 0.35 }, particleCount: 160, spread: 100, startVelocity: 45 });
  }, [fire]);

  return { claim, big };
}
